import { useCallback, useEffect, useRef, useState } from "react";
import { Form, Formik, FormikHelpers, useFormikContext } from "formik";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  Building2,
  Check,
  ChevronDown,
  ChevronUp,
  MapPin,
  Palette,
  Settings2,
} from "lucide-react";

import { EntityDetailsHeader } from "components/display/EntityDetailsHeader";
import { Button } from "components/ui/Button";
import { GenericLoading } from "components/ui/Loader";
import { InputFactory } from "factories/InputFactory";
import { AddressFormFieldNames } from "shared/enums/addressFormFieldNames";
import { plateformesAPI } from "services/swagger-api";
import { AddressDto, FileTypeEnum } from "api/actor";
import {
  applyValidationErrorsToForm,
  createPersonFieldResolver,
  parseApiError,
} from "shared/utils/apiValidationErrors";

import { CodeEntrepriseField } from "../components/CodeEntrepriseField";
import { CorporateAccountRecapModal } from "../components/CorporateAccountRecapModal";
import { createPlateforme } from "../api/plateformesApi";
import { plateformeFormConfig } from "../form/plateformeFormConfig";
import { plateformeInputsConfig, PlateformeInputFieldName } from "../form/plateformeInputsConfig";
import { PlateformeFormFieldNames } from "../form/PlateformeFormFieldNames";
import { PlateformeFormValues } from "../form/PlateformeFormValues";
import { CreateSectionCard } from "./CreateSectionCard";
import {
  ORGANIZATION_CREATE_SECTIONS,
  PlateformeCreateSectionsMenu,
} from "./PlateformeCreateSectionsMenu";
import { PlateformeRolesSection } from "./PlateformeRolesSection";
import { TypeCompagnieField } from "./TypeCompagnieField";
import { PlateformeBankAccountsSection } from "./PlateformeBankAccountsSection";
import { ModeGestion, ModeGestionCards, SourceTitulaire, SourceTitulaireCards } from "./CheminCreationCards";
import { PlateformeTitulaireExistantForm } from "./PlateformeTitulaireExistantForm";
import { PlateformeTitulaireCourtierForm } from "./PlateformeTitulaireCourtierForm";

interface CreatedCorporateAccount {
  plateformeId: string;
  codeEntreprise: string;
  designation: string;
  username: string;
  temporaryPassword: string;
}

/** Section (ancre) portant chaque champ — pour guider vers les erreurs de
 * validation, notamment celles des sections repliées ou hors écran. */
const FIELD_SECTIONS: [string[], string][] = [
  [['name', 'registrationNumber', 'capital', 'rcsCity', 'rcsNumber', 'oriasNumber', 'rcp'], 'section-identite'],
  [['address', 'additionalAddress', 'postalCode', 'city', 'country', 'googlePlaceId', 'latitude', 'longitude', 'phone', 'email'], 'section-coordonnees'],
  [['additionalRoles'], 'section-roles'],
  [['bankAccounts'], 'section-comptes-bancaires'],
  [['primaryColor', 'secondaryColor', 'backgroundColor', 'surfaceColor', 'accentColor', 'errorColor', 'successColor', 'warningColor', 'infoColor', 'fontFamily', 'logoFile', 'minimalLogoFile'], 'section-personnalisation'],
];

const sectionForField = (field: string): string | undefined =>
  FIELD_SECTIONS.find(([fields]) => fields.includes(field))?.[1];

/**
 * Formik ne déclenche pas onSubmit quand la validation échoue — sans retour,
 * « rien ne se passe ». Ce composant détecte les tentatives de soumission
 * invalides et remonte les champs en erreur à la page.
 */
const InvalidSubmitEffect: React.FC<{ onInvalidSubmit: (errorFields: string[]) => void }> = ({
  onInvalidSubmit,
}) => {
  const { submitCount, isValid, errors } = useFormikContext<PlateformeFormValues>();
  const lastHandledSubmit = useRef(0);

  useEffect(() => {
    if (submitCount > lastHandledSubmit.current && !isValid) {
      lastHandledSubmit.current = submitCount;
      onInvalidSubmit(Object.keys(errors));
    }
  }, [submitCount, isValid, errors, onInvalidSubmit]);

  return null;
};

const buildAddressDto = (values: PlateformeFormValues): AddressDto => ({
  line1: values.address,
  line2: values.additionalAddress || '',
  zipCode: values.postalCode,
  city: values.city,
  country: values.country,
  formattedAddress: `${values.address},${values.city},${values.country}`,
  latitude: values.latitude || undefined,
  longitude: values.longitude || undefined,
  googlePlaceId: values.googlePlaceId || undefined,
});

/**
 * Page de création d'une plateforme (+ sa PM corporate principale), dans
 * l'esprit de la page détail : header d'entité, menu latéral de sections
 * numérotées, sections pleine largeur empilées. Les futures sections
 * s'ajoutent dans ORGANIZATION_CREATE_SECTIONS + une carte dans la pile.
 */
export const PlateformeCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [created, setCreated] = useState<CreatedCorporateAccount | null>(null);
  // Branding replié par défaut : le thème a des valeurs par défaut, la
  // personnalisation reste possible ici ou plus tard sur la page détail.
  const [showBranding, setShowBranding] = useState(false);
  const [activeSection, setActiveSection] = useState(ORGANIZATION_CREATE_SECTIONS[0].id);
  // Assistant en deux choix : le MODE d'abord (gérée / administrée), puis la
  // SOURCE de la personne morale titulaire (existante / à créer).
  const [mode, setMode] = useState<ModeGestion | null>(null);
  const [source, setSource] = useState<SourceTitulaire | null>(null);

  const goBackToList = useCallback(
    () => navigate('/back-office/plateformes'),
    [navigate],
  );

  const renderField = useCallback((fieldName: PlateformeInputFieldName) => {
    return (
      <InputFactory config={plateformeInputsConfig} fieldName={fieldName} />
    );
  }, []);

  // Menu latéral : navigation par ancre (+ dépliage de la personnalisation).
  const navigateToSection = useCallback((id: string) => {
    if (id === 'section-personnalisation') {
      setShowBranding(true);
    }
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Soumission bloquée par la validation : signaler et guider vers la première
  // erreur (en dépliant la Personnalisation si elle en contient).
  const handleInvalidSubmit = useCallback((errorFields: string[]) => {
    if (errorFields.some((field) => sectionForField(field) === 'section-personnalisation')) {
      setShowBranding(true);
    }
    toast.error('Veuillez corriger les champs en erreur');

    const firstSection = errorFields.map(sectionForField).find(Boolean);
    if (firstSection) {
      setActiveSection(firstSection);
      // Laisse le DOM se déplier avant de scroller (cas Personnalisation).
      setTimeout(() => {
        document.getElementById(firstSection)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }, []);

  // Le grand formulaire à sections n'est rendu que sur le chemin Gérée + PM à
  // créer, une fois l'assistant passé et tant que l'écran final n'est pas là.
  const formVisible = mode === 'GEREE' && source === 'CREER' && !created;

  // Suivi de la section visible pour la mise en évidence du menu — attaché
  // seulement quand les sections existent dans le DOM (sinon l'observer se pose
  // dans le vide pendant l'assistant et ne se rattache jamais).
  useEffect(() => {
    if (!formVisible) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      { rootMargin: '-15% 0px -65% 0px' },
    );

    ORGANIZATION_CREATE_SECTIONS.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [formVisible]);

  const onSubmit = async (
    values: PlateformeFormValues,
    helpers: FormikHelpers<PlateformeFormValues>,
  ) => {
    setIsLoading(true);
    try {
      const response = await createPlateforme({
        name: values[PlateformeFormFieldNames.Name],
        code: values[PlateformeFormFieldNames.Code] || null,
        theme: {
          primaryColor: values[PlateformeFormFieldNames.PrimaryColor],
          secondaryColor: values[PlateformeFormFieldNames.SecondaryColor],
          backgroundColor: values[PlateformeFormFieldNames.BackgroundColor],
          surfaceColor: values[PlateformeFormFieldNames.SurfaceColor],
          accentColor: values[PlateformeFormFieldNames.AccentColor],
          errorColor: values[PlateformeFormFieldNames.ErrorColor],
          successColor: values[PlateformeFormFieldNames.SuccessColor],
          warningColor: values[PlateformeFormFieldNames.WarningColor],
          infoColor: values[PlateformeFormFieldNames.InfoColor],
          fontFamily: values[PlateformeFormFieldNames.FontFamily],
        },
        address: buildAddressDto(values),
        phone: values[PlateformeFormFieldNames.Phone],
        registrationNumber: values[PlateformeFormFieldNames.RegistrationNumber],
        capital: Number(values[PlateformeFormFieldNames.Capital]) || 0,
        rcsCity: values[PlateformeFormFieldNames.RcsCity],
        rcsNumber: values[PlateformeFormFieldNames.RcsNumber],
        oriasNumber: values[PlateformeFormFieldNames.OriasNumber],
        rcp: values[PlateformeFormFieldNames.Rcp] || null,
        email: values[PlateformeFormFieldNames.Email],
        additionalRoles: values[PlateformeFormFieldNames.AdditionalRoles] ?? [],
        sousNature: values[PlateformeFormFieldNames.SousNature] || null,
        bankAccounts: (values[PlateformeFormFieldNames.BankAccounts] ?? []).map((row) => ({
          iban: row.iban,
          label: row.label,
          bic: row.bic || null,
          isDefault: row.isDefault,
        })),
      });

      if (!response.isSuccess) {
        if (response.errorMessage) {
          toast.error(response.errorMessage);
        }
        return;
      }

      // Logos (endpoint dédié, best-effort) après création de la plateforme
      if (response.id && values.minimalLogoFile) {
        await plateformesAPI.uploadPlateformeLogo({
          plateformeId: response.id,
          fileType: FileTypeEnum.LogoMinimalPlateforme,
          file: values.minimalLogoFile,
        });
      }
      if (response.id && values.logoFile) {
        await plateformesAPI.uploadPlateformeLogo({
          plateformeId: response.id,
          fileType: FileTypeEnum.LogoPlateforme,
          file: values.logoFile,
        });
      }

      // Identifiants du compte corporate générés côté serveur — affichés une fois,
      // en écran final (jamais ré-affichés ensuite).
      if (response.data?.temporaryPassword && response.data.username) {
        setCreated({
          plateformeId: response.data.plateformeId,
          codeEntreprise: response.data.codeEntreprise,
          designation: values[PlateformeFormFieldNames.Name],
          username: response.data.username,
          temporaryPassword: response.data.temporaryPassword,
        });
      } else {
        goBackToList();
      }
    } catch (error) {
      console.error('Error on submit plateforme:', error);
      // Remonte les erreurs de validation du back (400) sur les champs concernés.
      const parsed = await parseApiError(error);
      const matched = applyValidationErrorsToForm({
        validationErrors: parsed.validationErrors,
        setFieldError: helpers.setFieldError,
        setFieldTouched: helpers.setFieldTouched,
        resolveField: createPersonFieldResolver(),
      });
      if (parsed.message || matched > 0) {
        toast.error(parsed.message || 'Veuillez corriger les champs en erreur');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Étapes 1 et 2 : mode de gestion, puis source de la personne morale titulaire.
  if (!mode || !source) {
    return (
      <section className="w-full overflow-hidden border bg-white rounded-2xl">
        <EntityDetailsHeader config={{
          icon: <Building2 className="text-primary" />,
          title: "Nouvelle plateforme",
          subtitle: !mode
            ? "Cette plateforme est-elle gérée par sa personne morale, ou administrée par vous ?"
            : mode === 'ADMINISTREE'
              ? "Plateforme administrée — la personne morale titulaire existe-t-elle déjà ?"
              : "Plateforme gérée — la personne morale gérante existe-t-elle déjà ?",
          infoSections: [],
        }} />
        <div className="p-8 shadow-inner flex flex-col gap-6">
          {!mode
            ? <ModeGestionCards onSelect={setMode} />
            : <SourceTitulaireCards mode={mode} onSelect={setSource} />}
          <div>
            <Button
              outline
              type="button"
              onClick={() => (mode ? setMode(null) : goBackToList())}
            >
              <ArrowLeft data-slot="icon" />
              {mode ? 'Retour' : 'Annuler'}
            </Button>
          </div>
        </div>
      </section>
    );
  }

  // PM existante (les deux modes) : formulaire léger, rien à provisionner.
  if (source === 'EXISTANTE') {
    return (
      <section className="w-full overflow-hidden border bg-white rounded-2xl">
        <EntityDetailsHeader config={{
          icon: <Building2 className="text-primary" />,
          title: "Nouvelle plateforme",
          subtitle: mode === 'ADMINISTREE'
            ? "Plateforme administrée — une personne morale existante en est titulaire"
            : "Une personne morale existante devient la gérante de la plateforme",
          infoSections: [],
        }} />
        <div className="p-8 shadow-inner">
          <PlateformeTitulaireExistantForm
            estAdministree={mode === 'ADMINISTREE'}
            onBack={() => setSource(null)}
          />
        </div>
      </section>
    );
  }

  // Administrée + cabinet à créer : plateforme + cabinet courtier en une opération.
  if (mode === 'ADMINISTREE') {
    return (
      <section className="w-full overflow-hidden border bg-white rounded-2xl">
        <EntityDetailsHeader config={{
          icon: <Building2 className="text-primary" />,
          title: "Nouvelle plateforme",
          subtitle: "Plateforme administrée — création du cabinet de courtage titulaire",
          infoSections: [],
        }} />
        <div className="p-8 shadow-inner">
          <PlateformeTitulaireCourtierForm onBack={() => setSource(null)} />
        </div>
      </section>
    );
  }

  // Écran final : identifiants du compte corporate (jamais ré-affichés).
  if (created) {
    return (
      <section className="w-full overflow-hidden border bg-white rounded-2xl">
        <div className="max-w-3xl mx-auto">
          <CorporateAccountRecapModal
            designation={created.designation}
            codeEntreprise={created.codeEntreprise}
            username={created.username}
            temporaryPassword={created.temporaryPassword}
          />
          <div className="flex justify-end gap-3 px-8 pb-8">
            <Button outline onClick={goBackToList}>
              <ArrowLeft data-slot="icon" />
              Retour aux plateformes
            </Button>
            {/* Suite naturelle : ajouter courtiers, habilitations, branding… */}
            <Button
              color="primary"
              onClick={() => navigate(`/back-office/plateformes/${created.plateformeId}`)}
            >
              <Settings2 data-slot="icon" />
              Configurer la plateforme
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative w-full overflow-hidden border bg-white rounded-2xl">
      {isLoading && <GenericLoading loading isParentSized />}

      <EntityDetailsHeader config={{
        icon: <Building2 className="text-primary" />,
        title: "Nouvelle plateforme",
        subtitle: "Identité, coordonnées et marque de la société",
        infoSections: [],
      }} />

      <div className="p-8 shadow-inner">
        <Formik
          initialValues={plateformeFormConfig.initialValues}
          validationSchema={plateformeFormConfig.validationSchema}
          onSubmit={onSubmit}
        >
          <Form>
            <InvalidSubmitEffect onInvalidSubmit={handleInvalidSubmit} />
            <div className="flex gap-6">
              <PlateformeCreateSectionsMenu
                activeId={activeSection}
                onNavigate={navigateToSection}
              />

              <div className="flex-1 min-w-0 flex flex-col gap-6">
                <CreateSectionCard
                  id="section-identite"
                  icon={Building2}
                  title="Identité"
                  subtitle="Informations légales de la société."
                >
                  <TypeCompagnieField />
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-1">
                      {renderField(PlateformeFormFieldNames.Name)}
                    </div>
                    <div className="col-span-1">
                      <CodeEntrepriseField
                        name={PlateformeFormFieldNames.Code}
                        autoGenerateIfEmpty
                      />
                    </div>
                    <div className="col-span-1">
                      {renderField(PlateformeFormFieldNames.RegistrationNumber)}
                    </div>
                    <div className="col-span-1">
                      {renderField(PlateformeFormFieldNames.Capital)}
                    </div>
                    <div className="col-span-1">
                      {renderField(PlateformeFormFieldNames.RcsCity)}
                    </div>
                    <div className="col-span-1">
                      {renderField(PlateformeFormFieldNames.RcsNumber)}
                    </div>
                    <div className="col-span-1">
                      {renderField(PlateformeFormFieldNames.OriasNumber)}
                    </div>
                    <div className="col-span-1">
                      {renderField(PlateformeFormFieldNames.Rcp)}
                    </div>
                  </div>
                </CreateSectionCard>

                <CreateSectionCard
                  id="section-coordonnees"
                  icon={MapPin}
                  title="Coordonnées"
                  subtitle="Adresse et contact de la plateforme."
                >
                  <div className="grid grid-cols-6 gap-4">
                    <div className="col-span-6">
                      {renderField(AddressFormFieldNames.Address)}
                    </div>
                    <div className="col-span-4">
                      {renderField(AddressFormFieldNames.AdditionalAddress)}
                    </div>
                    <div className="col-span-2">
                      {renderField(AddressFormFieldNames.PostalCode)}
                    </div>
                    <div className="col-span-2">
                      {renderField(AddressFormFieldNames.City)}
                    </div>
                    <div className="col-span-2">
                      {renderField(AddressFormFieldNames.Country)}
                    </div>
                    <div className="col-span-2">
                      {renderField(PlateformeFormFieldNames.Phone)}
                    </div>
                    <div className="col-span-6">
                      {renderField(PlateformeFormFieldNames.Email)}
                      <p className="mt-1 text-xs text-gray-500">
                        Un compte d&apos;accès sera créé automatiquement avec cet
                        email ; ses identifiants s&apos;affichent une seule fois
                        après la création.
                      </p>
                    </div>
                  </div>
                </CreateSectionCard>

                <PlateformeRolesSection />

                <PlateformeBankAccountsSection />

                {/* Personnalisation repliée par défaut : le thème part avec ses
                    valeurs par défaut, et tout reste modifiable sur la page détail. */}
                <div id="section-personnalisation" className="rounded-xl bg-section scroll-mt-6">
                  <button
                    type="button"
                    onClick={() => setShowBranding((visible) => !visible)}
                    className="w-full flex items-center gap-3 px-6 py-5 text-left group"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Palette className="w-5 h-5 text-primary" />
                    </div>
                    <span className="text-base font-bold text-content-dark flex-grow">
                      Personnalisation
                      <span className="ml-2 text-sm font-normal text-gray-500">
                        (optionnel — logos et thème, modifiables ensuite)
                      </span>
                    </span>
                    {showBranding
                      ? <ChevronUp className="w-5 h-5 text-gray-500" />
                      : <ChevronDown className="w-5 h-5 text-gray-500" />}
                  </button>

                  {showBranding && (
                    /* Champs rendus à plat (pas via ThemeLayout, dont les BaseSection
                       ajoutent un padding qui désalignerait le thème des logos). */
                    <div className="px-6 pb-6 space-y-6 border-t border-gray-200/70 pt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-1">
                          {renderField(PlateformeFormFieldNames.LogoFile)}
                        </div>
                        <div className="col-span-1">
                          {renderField(PlateformeFormFieldNames.MinimalLogoFile)}
                        </div>
                      </div>

                      <div>
                        <p className="text-lg font-bold text-content-dark mb-4">Thème</p>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-1">
                            {renderField(PlateformeFormFieldNames.PrimaryColor)}
                          </div>
                          <div className="col-span-1">
                            {renderField(PlateformeFormFieldNames.SecondaryColor)}
                          </div>
                          <div className="col-span-1">
                            {renderField(PlateformeFormFieldNames.AccentColor)}
                          </div>
                          <div className="col-span-1">
                            {renderField(PlateformeFormFieldNames.BackgroundColor)}
                          </div>
                          <div className="col-span-1">
                            {renderField(PlateformeFormFieldNames.SurfaceColor)}
                          </div>
                          <div className="col-span-1">
                            {renderField(PlateformeFormFieldNames.FontFamily)}
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className="text-lg font-bold text-content-dark mb-4">Couleurs de statut</p>
                        <div className="grid grid-cols-4 gap-4">
                          <div className="col-span-1">
                            {renderField(PlateformeFormFieldNames.ErrorColor)}
                          </div>
                          <div className="col-span-1">
                            {renderField(PlateformeFormFieldNames.SuccessColor)}
                          </div>
                          <div className="col-span-1">
                            {renderField(PlateformeFormFieldNames.WarningColor)}
                          </div>
                          <div className="col-span-1">
                            {renderField(PlateformeFormFieldNames.InfoColor)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between mt-2">
                  <Button outline type="button" onClick={() => setSource(null)}>
                    <ArrowLeft data-slot="icon" />
                    Retour
                  </Button>
                  <Button color="primary" type="submit" disabled={isLoading}>
                    <Check data-slot="icon" />
                    Créer la plateforme
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        </Formik>
      </div>
    </section>
  );
};
